<template>
    <div class="login-page" lang="vi">
        <main class="login-card" aria-labelledby="login-title">
            <img :src="`${$coroot.base_path}static/logo.svg`" class="coroot-logo" alt="Coroot" />
            <h1 id="login-title" class="login-title">{{ set_admin_password ? 'Thiết lập mật khẩu' : 'Đăng nhập' }}</h1>
            <p class="login-description">
                {{ set_admin_password ? 'Tạo mật khẩu cho tài khoản quản trị Coroot.' : 'Tiếp tục với tài khoản Coroot.' }}
            </p>

            <!-- Hide the local login form when SSO is required. -->
            <v-form v-if="!sso_forced" v-model="valid" @submit.prevent="post" ref="form" class="login-form">
                <label class="field-label" for="login-email">Tên đăng nhập hoặc email</label>
                <v-text-field
                    id="login-email"
                    v-model="form.email"
                    outlined
                    dense
                    light
                    type="text"
                    name="email"
                    autocomplete="username"
                    autocapitalize="off"
                    spellcheck="false"
                    placeholder="Email hoặc tên đăng nhập"
                    :rules="submitted ? [requiredField] : []"
                    :disabled="set_admin_password"
                    hide-details
                />

                <div class="password-label-row field-label-spaced">
                    <label class="field-label" for="login-password">Mật khẩu</label>
                    <button v-if="!set_admin_password" type="button" class="forgot-password-link" @click="openForgotPassword">Quên mật khẩu?</button>
                </div>
                <v-text-field
                    id="login-password"
                    v-model="form.password"
                    outlined
                    dense
                    light
                    name="password"
                    autocomplete="current-password"
                    placeholder="Nhập mật khẩu"
                    :type="show_password && form.password ? 'text' : 'password'"
                    :append-icon="form.password ? (show_password ? 'mdi-eye-off-outline' : 'mdi-eye-outline') : undefined"
                    @click:append="show_password = !show_password"
                    :rules="submitted ? [requiredField] : []"
                    hide-details
                />

                <template v-if="set_admin_password">
                    <label class="field-label field-label-spaced" for="login-confirm-password">Xác nhận mật khẩu</label>
                    <v-text-field
                        id="login-confirm-password"
                        v-model="confirm_password"
                        outlined
                        dense
                        light
                        autocomplete="new-password"
                        placeholder="Nhập lại mật khẩu"
                        :type="show_confirm_password && confirm_password ? 'text' : 'password'"
                        :append-icon="confirm_password ? (show_confirm_password ? 'mdi-eye-off-outline' : 'mdi-eye-outline') : undefined"
                        @click:append="show_confirm_password = !show_confirm_password"
                        :rules="submitted ? [requiredField, (v) => v === form.password || 'Mật khẩu xác nhận chưa khớp.'] : []"
                        hide-details
                    />
                </template>

                <v-alert v-if="validation_error || error" class="login-alert" color="red" icon="mdi-alert-circle-outline" outlined text>
                    {{ validation_error || error }}
                </v-alert>
                <v-alert v-else-if="message" class="login-alert" color="green" outlined text>
                    {{ message }}
                </v-alert>

                <v-btn block light type="submit" :disabled="loading" :loading="loading" height="48" class="login-submit">
                    <template v-if="set_admin_password">Thiết lập mật khẩu và đăng nhập</template>
                    <template v-else>Đăng nhập</template>
                </v-btn>
            </v-form>

            <template v-if="sso_enabled && !set_admin_password">
                <div class="sso-divider" aria-label="Hoặc đăng nhập bằng SSO">
                    <span></span>
                    <span>HOẶC</span>
                    <span></span>
                </div>

                <v-btn block light outlined height="48" class="sso-button" :href="ssoLoginUrl">
                    <span class="indexium-mark" aria-hidden="true">
                        <img :src="`${$coroot.base_path}static/indexium-mark.png`" alt="" />
                    </span>
                    <span>Đăng nhập bằng Indexium SSO</span>
                </v-btn>
            </template>

            <div v-if="sso_forced" class="login-note">Đăng nhập bằng mật khẩu đang tắt. Vui lòng tiếp tục bằng Indexium SSO.</div>
        </main>

        <v-dialog v-model="forgot_password_open" max-width="420">
            <v-card class="forgot-password-card" flat>
                <v-card-title class="forgot-password-title">Quên mật khẩu?</v-card-title>
                <v-card-text class="forgot-password-content">
                    <p>Nhập email hoặc tên đăng nhập để gửi yêu cầu đặt lại mật khẩu</p>
                    <v-form class="login-form" @submit.prevent>
                        <label class="field-label" for="forgot-password-email">Tên đăng nhập hoặc email</label>
                        <v-text-field
                            id="forgot-password-email"
                            v-model="forgot_password_email"
                            outlined
                            dense
                            light
                            type="text"
                            autocomplete="username"
                            autocapitalize="off"
                            spellcheck="false"
                            placeholder="Email hoặc tên đăng nhập"
                            hide-details
                        />
                        <div class="forgot-password-actions">
                            <v-btn text class="forgot-close-button" @click="forgot_password_open = false">Đóng</v-btn>
                            <v-btn type="submit" class="forgot-submit-button">Gửi yêu cầu</v-btn>
                        </div>
                    </v-form>
                </v-card-text>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
export default {
    data() {
        return {
            form: {
                email: '',
                password: '',
            },
            confirm_password: '',
            show_password: false,
            show_confirm_password: false,
            valid: true,
            submitted: false,
            error: '',
            validation_error: '',
            message: '',
            forgot_password_open: false,
            forgot_password_email: '',
            loading: false,
            sso_enabled: false,
            sso_forced: false,
        };
    },

    computed: {
        set_admin_password() {
            return this.$route.query.action === 'set_admin_password';
        },
        ssoLoginUrl() {
            const next = this.$route.query.next || '/';
            return this.$router.resolve({ path: this.$coroot.base_path + 'api/sso-login', query: { next } }).href;
        },
    },

    mounted() {
        this.checkSSOStatus();
        if (this.$route.query.sso_error) {
            this.error = 'Đăng nhập Indexium SSO chưa thành công. Vui lòng thử lại hoặc dùng tài khoản Coroot.';
        }
    },

    watch: {
        'form.password'(value) {
            if (!value) {
                this.show_password = false;
            }
            this.validation_error = '';
            this.error = '';
        },
        'form.email'() {
            this.validation_error = '';
            this.error = '';
        },
        confirm_password(value) {
            if (!value) {
                this.show_confirm_password = false;
            }
            this.validation_error = '';
            this.error = '';
        },
        set_admin_password: {
            handler(v) {
                if (v) {
                    this.form.email = 'admin';
                }
            },
            immediate: true,
        },
    },

    methods: {
        requiredField(value) {
            return !!String(value || '').trim() || 'Vui lòng nhập thông tin này.';
        },
        openForgotPassword() {
            this.forgot_password_email = this.form.email;
            this.forgot_password_open = true;
        },
        checkSSOStatus() {
            this.$api.ssoStatus((data, error) => {
                if (error) {
                    this.sso_enabled = false;
                    this.sso_forced = false;
                    return;
                }
                this.sso_enabled = data.enabled;
                this.sso_forced = data.force_sso || false;
            });
        },
        async post() {
            this.submitted = true;
            this.validation_error = '';
            await this.$nextTick();
            if (!this.$refs.form.validate()) {
                this.validation_error = this.set_admin_password ? 'Vui lòng nhập mật khẩu và xác nhận mật khẩu' : 'Vui lòng nhập email và mật khẩu';
                return;
            }

            this.loading = true;
            this.error = '';
            const form = { ...this.form };
            if (this.set_admin_password) {
                form.action = 'set_admin_password';
            }
            this.$api.login(form, (data, error) => {
                this.loading = false;
                if (error) {
                    const message = String(error).toLowerCase();
                    if (this.set_admin_password) {
                        this.error = message.includes('already been set')
                            ? 'Mật khẩu quản trị đã được thiết lập. Hãy tải lại trang để đăng nhập.'
                            : 'Chưa thể thiết lập mật khẩu. Vui lòng thử lại hoặc liên hệ quản trị viên.';
                    } else if (message.includes('invalid email or password')) {
                        this.error = 'Email hoặc mật khẩu không chính xác.';
                    } else {
                        this.error = 'Chưa thể đăng nhập lúc này. Vui lòng thử lại sau.';
                    }
                    return;
                }
                const next = this.$route.query.next;
                if (next && next.startsWith((this.$coroot.base_path || '/') + 'oauth/')) {
                    window.location.href = next;
                    return;
                }
                this.$router.push(next || { name: 'index' });
            });
        },
    },
};
</script>

<style scoped>
.login-page {
    --brightness: 100%;
    --text-color: #243b30;
    --text-color-dimmed: #738279;
    display: flex;
    width: 100%;
    min-height: 100vh;
    min-height: 100dvh;
    padding: 24px 16px;
    align-items: center;
    justify-content: center;
    background: linear-gradient(90deg, #124c39 0%, #2c805b 48%, #9ed7ad 100%);
    font-family:
        'Segoe UI',
        system-ui,
        -apple-system,
        BlinkMacSystemFont,
        Arial,
        sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
}

.login-card {
    width: 100%;
    max-width: 460px;
    padding: 32px 36px;
    border: 1px solid rgba(255, 255, 255, 0.65);
    border-radius: 0;
    background: #fff;
    box-shadow: 0 16px 48px rgba(8, 45, 29, 0.16);
}

.coroot-logo {
    display: block;
    width: 174px;
    max-width: 100%;
    height: auto;
    margin: 0 auto 18px;
}

.login-title {
    margin: 0;
    color: #20382c;
    font-size: 28px;
    font-weight: 700;
    letter-spacing: -0.02em;
    line-height: 1.3;
    text-align: center;
}

.login-description {
    margin: 8px 0 24px;
    color: #7b8980;
    font-size: 14px;
    line-height: 1.5;
    text-align: center;
}

.login-alert {
    width: 100%;
    height: auto;
    min-height: 36px;
    margin: 12px 0 0;
    padding: 7px 12px;
    border-radius: 0;
    box-sizing: border-box;
    font-size: 13px;
    line-height: 1.45;
}

.login-alert ::v-deep .v-alert__wrapper {
    align-items: flex-start;
}

.login-alert ::v-deep .v-alert__icon {
    margin-top: 1px;
    margin-right: 10px;
    font-size: 18px;
}

.login-alert ::v-deep .v-alert__content {
    min-width: 0;
    font-weight: 600;
    white-space: normal;
    overflow-wrap: anywhere;
}

.field-label {
    display: block;
    margin-bottom: 8px;
    color: #35483d;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.5;
}

.password-label-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
}

.password-label-row .field-label {
    margin-bottom: 8px;
}

.forgot-password-link {
    padding: 0;
    border: 0;
    background: transparent;
    color: #2a925c;
    cursor: pointer;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.5;
}

.forgot-password-link:hover,
.forgot-password-link:focus-visible {
    color: #1c6836;
    text-decoration: underline;
}

.field-label-spaced {
    margin-top: 16px;
}

.forgot-password-card {
    padding: 24px;
    border-radius: 0 !important;
    background: #fff;
}

.forgot-password-title {
    min-height: auto;
    padding: 0 0 12px;
    color: #20382c;
    font-size: 22px;
    font-weight: 700;
    line-height: 1.3;
}

.forgot-password-content {
    padding: 0;
    color: #738279;
}

.forgot-password-content p {
    margin: 0 0 18px;
    font-size: 14px;
    line-height: 1.5;
}

.forgot-password-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 20px;
}

.forgot-password-actions .v-btn {
    border-radius: 0;
    font-family: inherit;
    letter-spacing: 0;
    text-transform: none;
}

.forgot-password-actions .forgot-submit-button.v-btn {
    background: #258444 !important;
    color: #fff !important;
    font-weight: 700;
}

.forgot-password-actions .forgot-submit-button.v-btn:hover {
    background: #1c6836 !important;
}

.login-form ::v-deep .v-input__slot {
    min-height: 48px !important;
    margin-bottom: 0;
    border-radius: 0 !important;
    background: #fff !important;
}

.login-form ::v-deep fieldset {
    border-color: #d6e1d9;
}

.login-form ::v-deep .error--text fieldset {
    border-color: #e57373 !important;
}

.login-form ::v-deep .v-input--is-focused fieldset {
    border-color: #2a925c !important;
}

.login-form ::v-deep .v-input__slot input {
    color: #30473a !important;
    -webkit-text-fill-color: #30473a !important;
    caret-color: #2a925c;
    font-family: inherit;
    font-size: 15px;
    line-height: 1.5;
}

.login-form ::v-deep .v-input__slot input::placeholder {
    color: #a2afa6 !important;
    -webkit-text-fill-color: #a2afa6 !important;
    opacity: 1;
}

.login-form ::v-deep .v-input__append-inner {
    align-self: stretch;
    align-items: center;
    margin-top: 0 !important;
}

.login-form ::v-deep .v-input__icon--append .v-icon {
    color: #929c95 !important;
    font-size: 21px;
    cursor: pointer;
}

.login-form ::v-deep .v-input__icon--append .v-icon:hover {
    color: #617469 !important;
}

.login-page .login-submit.v-btn {
    margin-top: 22px;
    border-radius: 0;
    background-color: #258444 !important;
    border-color: #258444 !important;
    color: #fff !important;
    box-shadow: none;
    font-family: inherit;
    font-size: 15px !important;
    font-weight: 700 !important;
    letter-spacing: 0;
    text-transform: none;
    transition: background-color 0.18s ease;
}

.login-page .login-submit.v-btn:hover,
.login-page .login-submit.v-btn:focus-visible {
    background-color: #1c6836 !important;
    border-color: #1c6836 !important;
}

.login-page .login-submit.v-btn.v-btn--disabled {
    color: #fff !important;
    opacity: 1;
}

.sso-divider {
    display: flex;
    align-items: center;
    gap: 14px;
    margin: 20px 0;
    color: #99a69d;
    font-size: 11px;
    line-height: 1;
    letter-spacing: 0.08em;
}

.sso-divider span:first-child,
.sso-divider span:last-child {
    height: 1px;
    flex: 1;
    background: #e5ece7;
}

.login-page .sso-button.v-btn {
    max-width: 100%;
    padding: 0 12px;
    border-color: #d6e1d9 !important;
    border-radius: 0;
    color: #607568 !important;
    font-family: inherit;
    font-size: 14px !important;
    font-weight: 600 !important;
    letter-spacing: 0;
    text-transform: none;
    transition:
        border-color 0.18s ease,
        background-color 0.18s ease;
}

.sso-button ::v-deep .v-btn__content {
    gap: 10px;
    white-space: normal;
    line-height: 1.4;
}

.sso-button:hover {
    border-color: #88b89d !important;
    background: #f3f9f5;
}

.indexium-mark {
    display: inline-flex;
    width: 24px;
    height: 24px;
    flex: 0 0 24px;
    align-items: center;
    justify-content: center;
}

.indexium-mark img {
    display: block;
    width: 24px;
    height: 24px;
    object-fit: contain;
}

.login-note {
    margin-top: 16px;
    color: #748078;
    font-size: 13px;
    line-height: 1.5;
    text-align: center;
}

@media (max-width: 480px) {
    .login-page {
        padding: 16px;
    }

    .login-card {
        padding: 28px 24px;
        border-radius: 0;
    }
}

@media (max-height: 620px) {
    .login-page {
        padding: 12px;
    }

    .login-card {
        padding: 20px 28px;
    }

    .coroot-logo {
        width: 148px;
        margin-bottom: 12px;
    }

    .login-title {
        font-size: 25px;
    }

    .login-description {
        margin: 6px 0 18px;
    }

    .field-label-spaced {
        margin-top: 12px;
    }

    .login-page .login-submit.v-btn {
        margin-top: 16px;
    }

    .sso-divider {
        margin: 16px 0;
    }
}

@media (max-height: 520px) {
    .login-alert {
        min-height: 32px;
        padding: 5px 8px;
    }
}

@media (max-width: 360px) {
    .login-card {
        padding-right: 18px;
        padding-left: 18px;
    }

    .login-page .sso-button.v-btn {
        font-size: 13px !important;
    }
}

@media (max-height: 520px) {
    .login-card {
        padding: 16px 24px;
    }

    .coroot-logo {
        width: 128px;
        margin-bottom: 10px;
    }

    .login-title {
        margin-bottom: 18px;
        font-size: 22px;
    }

    .login-description {
        display: none;
    }

    .field-label {
        margin-bottom: 6px;
        font-size: 13px;
        line-height: 1.4;
    }

    .field-label-spaced {
        margin-top: 10px;
    }

    .login-form ::v-deep .v-input__slot {
        min-height: 42px !important;
    }

    .login-page .login-submit.v-btn,
    .login-page .sso-button.v-btn {
        height: 42px !important;
    }

    .sso-divider {
        margin: 12px 0;
    }
}

@media (max-height: 400px) {
    .login-page {
        align-items: flex-start;
        padding: 8px 12px;
        overflow-y: auto;
    }

    .login-card {
        padding: 8px 20px;
    }

    .coroot-logo {
        width: 96px;
        margin-bottom: 2px;
    }

    .login-title {
        margin-bottom: 6px;
        font-size: 18px;
        line-height: 1.2;
    }

    .login-description {
        display: none;
    }

    .field-label {
        margin-bottom: 0;
        font-size: 12px;
        line-height: 1.35;
    }

    .forgot-password-link {
        font-size: 12px;
    }

    .field-label-spaced {
        margin-top: 2px;
    }

    .login-form ::v-deep .v-input {
        margin-top: 0;
        padding-top: 0;
    }

    .login-form ::v-deep .v-input__slot {
        min-height: 34px !important;
    }

    .login-form ::v-deep .v-input__slot input {
        font-size: 13px;
    }

    .login-alert {
        min-height: 26px;
        margin: 6px auto 0;
        padding: 4px 8px;
        font-size: 12px;
    }

    .login-page .login-submit.v-btn,
    .login-page .sso-button.v-btn {
        height: 36px !important;
    }

    .login-page .login-submit.v-btn {
        margin-top: 6px;
        font-size: 13px !important;
    }

    .sso-divider {
        gap: 10px;
        margin: 6px 0;
    }

    .indexium-mark,
    .indexium-mark img {
        width: 20px;
        height: 20px;
    }
}
</style>
